import { Link } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Shield, Lock, Plane, Vault, ArrowRight, Search, Globe2, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/apiClient";
import ReviewsCarousel from "@/components/home/ReviewsCarousel";

const IMG_HERO = "https://images.unsplash.com/photo-1582139329536-e7284fece509?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHx2YXVsdHxlbnwwfHx8fDE3ODk4NTU2MDR8MA&ixlib=rb-4.1.0&q=85";
const IMG_CONT = "https://images.unsplash.com/photo-1605732562742-3023a888e56e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxzaGlwcGluZyUyMGNvbnRhaW5lcnxlbnwwfHx8fDE3ODk4NTU2MDR8MA&ixlib=rb-4.1.0&q=85";

export default function HomePage() {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { t } = useTranslation();

  const services = [
    { icon: Vault, title: t("home.svc_vault_t"), copy: t("home.svc_vault_c"), image: "/services/vaulting.webp", alt: "" },
    { icon: Lock, title: t("home.svc_pouch_t"), copy: t("home.svc_pouch_c"), image: "/services/pouch.webp", alt: "" },
    { icon: Plane, title: t("home.svc_air_t"), copy: t("home.svc_air_c"), image: "/services/aircraft.webp", alt: "" },
    { icon: Globe2, title: t("home.svc_intl_t"), copy: t("home.svc_intl_c"), image: "/services/customs.webp", alt: "" },
  ];

  const [stats, setStats] = useState({ routes: 92, sea: 18, air: 34, ground: 247 });
  const tickTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const scheduleNext = (nextTickAt) => {
      if (!nextTickAt) return;
      const ms = new Date(nextTickAt).getTime() - Date.now();
      const delay = Math.max(1000, Math.min(ms + 1000, 30 * 24 * 3600 * 1000));
      clearTimeout(tickTimerRef.current);
      tickTimerRef.current = setTimeout(load, delay);
    };
    const load = async () => {
      try {
        const { data } = await api.get("/stats/live");
        if (cancelled) return;
        setStats({ routes: data.routes, sea: data.sea, air: data.air, ground: data.ground });
        scheduleNext(data.next_tick_at);
      } catch {
        clearTimeout(tickTimerRef.current);
        tickTimerRef.current = setTimeout(load, 5 * 60 * 1000);
      }
    };
    load();
    return () => { cancelled = true; clearTimeout(tickTimerRef.current); };
  }, []);

  const modes = useMemo(
    () => [
      { l: t("home.sea"), v: t("home.sea_v", { count: stats.sea }) },
      { l: t("home.air"), v: t("home.air_v", { count: stats.air }) },
      { l: t("home.ground"), v: t("home.ground_v", { count: stats.ground }) },
    ],
    [stats, t]
  );

  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/track/${encodeURIComponent(q.trim().toUpperCase())}`);
  };

  return (
    <>
      <section className="relative overflow-hidden dvl-grain" data-testid="home-hero">
        <div className="absolute inset-0">
          <img src={IMG_HERO} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1128]/70 via-[#0A1128]/85 to-[#0A1128]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-28">
          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,540px)] gap-14 items-center">
            <div className="dvl-rise">
              <div className="inline-flex items-center gap-2 border border-amber-500/40 rounded-full px-4 py-1.5 mb-8 bg-amber-500/5">
                <BadgeCheck className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-mono uppercase tracking-[0.25em] text-amber-300">
                  {t("home.badge")}
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight text-slate-50 leading-tight">
                {t("home.title_1")} <span className="italic text-amber-400">{t("home.title_italic")}</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
                {t("home.subtitle")}
              </p>

              <form
                onSubmit={submit}
                className="mt-10 max-w-xl bg-[#0F1C3F]/70 backdrop-blur-md border border-amber-500/30 rounded-2xl p-3 flex flex-col sm:flex-row gap-3 shadow-2xl shadow-black/50"
                data-testid="hero-track-form"
              >
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-400/70" />
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={t("home.hero_placeholder")}
                    data-testid="hero-tracking-input"
                    className="bg-slate-950/80 border-amber-500/30 text-slate-100 placeholder-slate-500 pl-9 font-mono h-12"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="hero-track-submit"
                  className="h-12 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-semibold px-6"
                >
                  {t("home.hero_button")} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </form>
              <div className="mt-4 flex flex-wrap gap-2 text-xs font-mono uppercase tracking-widest text-slate-500">
                {t("home.hero_try")}
                {["DVL-8942-CH", "DVL-3109-UK", "DVL-7721-AE", "DVL-9045-SG"].map((tid) => (
                  <button
                    key={tid}
                    onClick={() => navigate(`/track/${tid}`)}
                    className="text-amber-400/80 hover:text-amber-300 underline-offset-4 hover:underline"
                    data-testid={`sample-track-${tid}`}
                  >
                    {tid}
                  </button>
                ))}
              </div>
            </div>

            <div className="relative dvl-rise" style={{ animationDelay: "0.15s" }} data-testid="hero-multimodal">
              <div className="absolute -inset-6 bg-amber-500/10 rounded-[2rem] blur-3xl" />
              <div className="relative rounded-2xl overflow-hidden border border-amber-500/30 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
                <img
                  src="/hero-multimodal.webp"
                  alt=""
                  className="w-full h-[380px] lg:h-[460px] object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/70 via-transparent to-transparent" />
                <div className="absolute inset-0 ring-1 ring-inset ring-amber-500/20 rounded-2xl pointer-events-none" />
                <div className="absolute top-4 left-4 flex items-center gap-2 bg-[#0A1128]/80 backdrop-blur-md border border-amber-500/30 rounded-full px-3 py-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 dvl-pulse" />
                  <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-300" data-testid="live-corridor-badge">
                    {t("home.live_corridor", { count: stats.routes })}
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 right-4 grid grid-cols-3 gap-2">
                  {modes.map((m) => (
                    <div key={m.l} data-testid={`stat-${m.l.toLowerCase()}`} className="bg-[#0A1128]/85 backdrop-blur-md border border-amber-500/25 rounded-lg px-3 py-2">
                      <div className="text-[9px] font-mono uppercase tracking-widest text-amber-400/80">{m.l}</div>
                      <div className="text-sm font-serif text-slate-50">{m.v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-amber-500/15 bg-[#0F1C3F]/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            ["27yrs", t("home.stat_years")],
            ["92", t("home.stat_corridors")],
            ["$4.8B", t("home.stat_assets")],
            ["0", t("home.stat_breaches")],
          ].map(([n, l]) => (
            <div key={l}>
              <div className="font-serif text-3xl md:text-4xl text-amber-400">{n}</div>
              <div className="mt-1 text-xs font-mono uppercase tracking-widest text-slate-400">{l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="home-services">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div className="max-w-xl">
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-amber-400/80 mb-3">
              {t("home.practice")}
            </div>
            <h2 className="text-3xl md:text-4xl font-serif font-semibold tracking-tight">
              {t("home.practice_title")}
            </h2>
          </div>
          <Link to="/services" className="text-amber-400 hover:text-amber-300 text-sm font-mono uppercase tracking-widest inline-flex items-center gap-2">
            {t("home.view_all")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s, i) => (
            <div
              key={i}
              className="group bg-[#0F1C3F]/80 backdrop-blur-md border border-amber-500/20 hover:border-amber-500/50 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
              data-testid={`home-service-${i}`}
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={s.image} alt={s.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F1C3F] via-[#0F1C3F]/40 to-transparent" />
              </div>
              <div className="p-6">
                <div className="w-11 h-11 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-4 -mt-11 relative z-10 backdrop-blur-md">
                  <s.icon className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className="font-serif text-xl text-slate-100 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{s.copy}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-amber-500/15">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-amber-400/80 mb-3">
              {t("home.chain_kicker")}
            </div>
            <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-6">
              {t("home.chain_title")}
            </h2>
            <p className="text-slate-300 leading-relaxed mb-8">
              {t("home.chain_body")}
            </p>
            <ul className="space-y-4">
              {[t("home.chain_f1"), t("home.chain_f2"), t("home.chain_f3"), t("home.chain_f4")].map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Shield className="w-4 h-4 text-amber-400 mt-1" />
                  <span className="text-slate-200">{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 bg-amber-500/5 rounded-3xl blur-3xl" />
            <img src={IMG_CONT} alt="" className="relative rounded-2xl border border-amber-500/25 shadow-2xl shadow-black/40" />
          </div>
        </div>
      </section>

      <ReviewsCarousel />

      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-24 text-center">
        <h2 className="text-3xl md:text-5xl font-serif font-semibold mb-6">
          {t("home.cta_title")}
        </h2>
        <p className="text-slate-300 max-w-2xl mx-auto mb-10">
          {t("home.cta_body")}
        </p>
        <div className="flex justify-center gap-3 flex-wrap">
          <Link to="/contact" data-testid="cta-contact" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-semibold px-7 py-3 rounded-lg transition-all active:scale-95">
            {t("home.cta_contact")}
          </Link>
          <Link to="/services" className="border border-amber-500/40 hover:border-amber-400 text-amber-300 px-7 py-3 rounded-lg transition-colors">
            {t("home.cta_services")}
          </Link>
        </div>
      </section>
    </>
  );
}
